import { Vector3 } from "three";

import type { MuseumContent } from "./contracts";

export type RoutePassage = {
  axis: "x" | "z";
  min: number;
  max: number;
  crossAxisValue: number;
  minRoomId: string;
  maxRoomId: string;
};

export type RouteGuidance = {
  distance: number;
  direction: Vector3;
  viaPassage: boolean;
};

export type RouteTarget = {
  order: number;
  artworkId: string;
  roomId: string;
  title: string;
  position: Vector3;
};

export class RouteManager {
  readonly targets: readonly RouteTarget[];
  readonly visited = new Set<string>();
  readonly #passage: RoutePassage | null;
  #activeOrder = 1;
  #completed = false;

  constructor(
    content: MuseumContent,
    positions: ReadonlyMap<string, Vector3>,
    passage: RoutePassage | null,
  ) {
    this.#passage = passage;
    this.targets = content.route.map((stop, index) => {
      const artwork = content.artworks[index];
      const position = positions.get(stop.artwork_id);
      if (artwork === undefined || position === undefined) {
        throw new Error(`GLB_ROUTE_MISMATCH: ${stop.artwork_id}`);
      }
      return {
        order: stop.order,
        artworkId: stop.artwork_id,
        roomId: stop.room_id,
        title: artwork.title,
        position: position.clone(),
      };
    });
  }

  get activeOrder(): number {
    return this.#activeOrder;
  }

  get active(): RouteTarget {
    const target = this.targets[this.#activeOrder - 1];
    if (target === undefined) throw new Error("ROUTE_STATE_INVALID");
    return target;
  }

  get completed(): boolean {
    return this.#completed;
  }

  previous(): void {
    this.#activeOrder = Math.max(1, this.#activeOrder - 1);
    this.#completed = false;
  }

  next(): void {
    this.#activeOrder = Math.min(this.targets.length, this.#activeOrder + 1);
  }

  setActive(order: number): void {
    if (!Number.isInteger(order) || order < 1 || order > this.targets.length) return;
    this.#activeOrder = order;
    this.#completed = false;
  }

  visit(artworkId: string): void {
    this.visited.add(artworkId);
    if (this.active.artworkId !== artworkId) return;
    const next = this.targets.find(
      (target) => target.order > this.#activeOrder && !this.visited.has(target.artworkId),
    );
    if (next !== undefined) this.#activeOrder = next.order;
    else if (this.#activeOrder === this.targets.length) this.#completed = true;
  }

  guidance(visitor: Vector3): RouteGuidance {
    const waypoints = this.#guidanceWaypoints(visitor);
    const first = waypoints[0] ?? this.active.position;
    const direction = first.clone().sub(visitor);
    direction.y = 0;
    if (direction.lengthSq() > 0) direction.normalize();
    let distance = 0;
    let previous = visitor;
    for (const waypoint of waypoints) {
      distance += horizontalDistance(previous, waypoint);
      previous = waypoint;
    }
    return { distance, direction, viaPassage: waypoints.length > 1 };
  }

  #guidanceWaypoints(visitor: Vector3): Vector3[] {
    const passage = this.#passage;
    const target = this.active.position.clone();
    if (passage === null) return [target];
    const targetOnMaxSide = this.active.roomId === passage.maxRoomId;
    const targetOnMinSide = this.active.roomId === passage.minRoomId;
    if (!targetOnMaxSide && !targetOnMinSide) return [target];

    const clearance = 0.55;
    const minEntry = passagePoint(passage, passage.min - clearance, visitor.y);
    const maxEntry = passagePoint(passage, passage.max + clearance, visitor.y);
    const coordinate = visitor[passage.axis];

    if (targetOnMaxSide && coordinate < passage.max + clearance) {
      if (coordinate < passage.min && horizontalDistance(visitor, minEntry) > 0.6) {
        return [minEntry, maxEntry, target];
      }
      return [maxEntry, target];
    }
    if (targetOnMinSide && coordinate > passage.min - clearance) {
      if (coordinate > passage.max && horizontalDistance(visitor, maxEntry) > 0.6) {
        return [maxEntry, minEntry, target];
      }
      return [minEntry, target];
    }
    return [target];
  }
}

/**
 * The two points that get a walker from one room to the other without clipping a wall:
 * the mouth of the corridor on the near side and the mouth on the far side.
 *
 * `RouteManager` derives this per frame from wherever the visitor happens to be. A tour
 * has no visitor to react to — it lays its whole path out in advance — so it asks for
 * the crossing by name instead. Returns nothing when both ends are in the same room, or
 * when either room is not one the corridor joins.
 */
export function passageCrossing(
  passage: RoutePassage,
  fromRoomId: string,
  toRoomId: string,
  y: number,
): Vector3[] {
  if (fromRoomId === toRoomId) return [];
  const clearance = 0.55;
  const minEntry = passagePoint(passage, passage.min - clearance, y);
  const maxEntry = passagePoint(passage, passage.max + clearance, y);
  if (fromRoomId === passage.minRoomId && toRoomId === passage.maxRoomId) {
    return [minEntry, maxEntry];
  }
  if (fromRoomId === passage.maxRoomId && toRoomId === passage.minRoomId) {
    return [maxEntry, minEntry];
  }
  // Starting in the corridor itself: aim at the mouth on the destination's side only.
  if (toRoomId === passage.maxRoomId) return [maxEntry];
  if (toRoomId === passage.minRoomId) return [minEntry];
  return [];
}

function passagePoint(passage: RoutePassage, coordinate: number, y: number): Vector3 {
  return passage.axis === "x"
    ? new Vector3(coordinate, y, passage.crossAxisValue)
    : new Vector3(passage.crossAxisValue, y, coordinate);
}

function horizontalDistance(left: Vector3, right: Vector3): number {
  return Math.hypot(right.x - left.x, right.z - left.z);
}
